import { useState, useEffect, useCallback, useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  RefreshCw,
  WifiOff,
  KeyRound,
  Loader2,
  UserCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import * as dbOps from '../db/kioskDbOperations';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(date) {
  if (!date) return 'Never';
  const now = Date.now();
  const diffMs = now - new Date(date).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// EmployeeAvatar
// ---------------------------------------------------------------------------

function EmployeeAvatar({ employee }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (employee.avatarBlob) {
      try {
        const url = URL.createObjectURL(employee.avatarBlob);
        setImgSrc(url);
        return () => URL.revokeObjectURL(url);
      } catch {
        // Fall back to URL or initials
      }
    }

    if (employee.avatarUrl) {
      setImgSrc(employee.avatarUrl);
    }
  }, [employee.avatarBlob, employee.avatarUrl]);

  if (imgSrc && !imgError) {
    return (
      <img
        src={imgSrc}
        alt={employee.displayName || 'Employee'}
        className="w-10 h-10 rounded-full object-cover bg-slate-200 flex-none"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-none">
      <span className="text-sm font-semibold text-slate-500">
        {getInitials(employee.displayName)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmployeeRow
// ---------------------------------------------------------------------------

function EmployeeRow({ employee }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
    >
      <EmployeeAvatar employee={employee} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">
          {employee.displayName || 'Unknown'}
        </p>
        {employee.department && (
          <p className="text-xs text-slate-400 truncate">{employee.department}</p>
        )}
      </div>

      {employee.kioskPin ? (
        <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-200 flex-none">
          <KeyRound className="w-3 h-3" />
          PIN set
        </Badge>
      ) : (
        <Badge variant="outline" className="gap-1 text-slate-400 flex-none">
          No PIN
        </Badge>
      )}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// KioskAdminEmployees
// ---------------------------------------------------------------------------

export default function KioskAdminEmployees({ syncEngine }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // -----------------------------------------------------------------------
  // Load employees from IndexedDB
  // -----------------------------------------------------------------------
  const loadEmployees = useCallback(async () => {
    try {
      const [emps, syncTime] = await Promise.all([
        dbOps.getAllEmployees(),
        dbOps.getConfig('last_employee_sync'),
      ]);
      setEmployees(emps);
      setLastSync(syncTime);
    } catch (err) {
      console.error('Failed to load employees:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // -----------------------------------------------------------------------
  // Refresh from server
  // -----------------------------------------------------------------------
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await syncEngine.refreshEmployeesFromServer();
      await loadEmployees();
    } catch (err) {
      console.error('Failed to refresh employees:', err);
    } finally {
      setRefreshing(false);
    }
  }, [syncEngine, loadEmployees]);

  // -----------------------------------------------------------------------
  // Filtered employees
  // -----------------------------------------------------------------------
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;

    const q = searchQuery.toLowerCase().trim();
    return employees.filter((emp) => {
      const name = (emp.displayName || '').toLowerCase();
      const dept = (emp.department || '').toLowerCase();
      return name.includes(q) || dept.includes(q);
    });
  }, [employees, searchQuery]);

  // -----------------------------------------------------------------------
  // Sorted alphabetically by display name
  // -----------------------------------------------------------------------
  const sortedEmployees = useMemo(() => {
    return [...filteredEmployees].sort((a, b) => {
      const nameA = (a.displayName || '').toLowerCase();
      const nameB = (b.displayName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [filteredEmployees]);

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Employees</h1>
          <p className="text-sm text-slate-500 mt-1">
            {loading ? (
              <Skeleton className="h-4 w-40 inline-block" />
            ) : (
              <>
                {employees.length} employee{employees.length !== 1 ? 's' : ''} cached
                {lastSync && (
                  <span className="text-slate-400">
                    {' '}&middot; Updated {formatRelativeTime(lastSync)}
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        <Button
          variant="outline"
          className="gap-2 flex-none"
          disabled={!syncEngine.isOnline || refreshing}
          onClick={handleRefresh}
        >
          {refreshing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* Offline notice */}
      {!syncEngine.isOnline && (
        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
          <WifiOff className="w-4 h-4 flex-none" />
          Offline. Showing cached employee data. Refresh is unavailable until online.
        </div>
      )}

      {/* Employee list card */}
      <Card className="py-0 overflow-hidden">
        {/* Search bar */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or department..."
              className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-colors"
              aria-label="Search employees"
            />
          </div>
        </div>

        {/* List */}
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
          {loading ? (
            <div className="space-y-0 divide-y divide-slate-100">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-md" />
                </div>
              ))}
            </div>
          ) : sortedEmployees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              {searchQuery ? (
                <>
                  <Search className="w-10 h-10 mb-3 opacity-50" />
                  <p className="text-sm font-medium">No employees match your search</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </>
              ) : (
                <>
                  <UserCircle className="w-10 h-10 mb-3 opacity-50" />
                  <p className="text-sm font-medium">No employees cached</p>
                  <p className="text-xs mt-1">
                    Tap Refresh to download employee data from the server
                  </p>
                </>
              )}
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {sortedEmployees.map((emp) => (
                <EmployeeRow key={emp.id} employee={emp} />
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Footer with count */}
        {!loading && filteredEmployees.length > 0 && searchQuery && (
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400">
              Showing {filteredEmployees.length} of {employees.length} employee{employees.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
